import { Box, Theme, Typography } from "@outschool/backpack";
import {
  ActivityEnrollableTimesModal_ActivityFragment,
  ClassDetailsNoSectionsQuery,
  ClassDetailsQuery_ActivityFragment
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import React, { Dispatch, RefObject, SetStateAction, memo } from "react";

import ActivityEnrollableTimes from "../activityEnrollableTimes/ActivityEnrollableTimes";
import AvailableTimes from "../availableTimes/AvailableTimes";
import SelfPacedAvailableNow from "../SelfPacedAvailableNow";
import { TabConfigType } from "../subcomponents/ClassDetailsNav";
import HeadingMarker from "../subcomponents/HeadingMarker";
import { TabValue } from "../subcomponents/PublicReviewsOfActivity";
import TeacherDetails from "../subcomponents/TeacherDetails";

export interface BaseClassDetailsMainBodyProps {
  // props
  activity: ClassDetailsQuery_ActivityFragment;
  canEnroll: boolean;
  classNavTabs?: TabConfigType[];
  facebookAppId?: string;
  hasOneOnOneAvailabilities: boolean;
  isAvailabilityModalOpen?: boolean;
  isLoadingOneOnOneAvailabilities: boolean;
  leader: ClassDetailsNoSectionsQuery["activity"]["leader"];
  onClickViewReviews?: () => void;
  setIsAvailabilityModalOpen?: React.Dispatch<SetStateAction<boolean>>;
  setTabValue?: React.Dispatch<React.SetStateAction<TabValue>>;
  showAvailableTimes: boolean;
  tabValue?: TabValue;
  // refs
  sectionsRef: React.RefObject<HTMLElement>;
  // memos & elements
  ActivityEnrollableTimes?: React.JSX.Element | null;
  AskAQuestionButton?: React.JSX.Element;
  AvailableTimes?: React.JSX.Element | null;
  TeacherSection?: React.JSX.Element;
  TutorEnrollButton?: React.ElementType;
}

export interface ClassDetailsReviewsProps {
  activity: ClassDetailsQuery_ActivityFragment;
  reviewsRef: RefObject<HTMLElement>;
  setTabValue: Dispatch<SetStateAction<TabValue>>;
  tabValue: TabValue;
}

export interface DesktopClassDetailsMainBodyProps
  extends BaseClassDetailsMainBodyProps {
  // props
  activityWithEnrollableTimes?: ActivityEnrollableTimesModal_ActivityFragment;
  isActivitySavedByCurrentUser?: boolean;
  isCrossSellVariant?: boolean;
  isOrganizationEnrollment: boolean;
  onClickMoreLikeThis: () => void;
  onClickViewTeacher?: (event: React.SyntheticEvent) => void;
  // refs
  refsContainer: any;
  viewAvailabilityRef: React.RefObject<HTMLElement>;
  // memos & elements
}

export interface MobileClassDetailsMainBodyProps
  extends BaseClassDetailsMainBodyProps {
  refsContainer: any;
  // memos & elements
  BecomeAMemberBanner?: React.ReactElement;
}

export const gradientFadeStyle = {
  position: "relative",
  maxHeight: "12em",
  overflow: "hidden",
  "&:after": {
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    height: "100%",
    width: "100%",
    content: '""',
    pointerEvents: "none",
    background:
      "linear-gradient(rgba(255, 255, 255, 0) 75%, rgb(255, 255, 255) 100%)"
  }
};

export const ActivityEnrollableTimesMemo = memo(
  function ActivityEnrollableTimesBody({
    activityWithEnrollableTimes,
    leader,
    onEnrollClick
  }: {
    activityWithEnrollableTimes?: ActivityEnrollableTimesModal_ActivityFragment;
    leader: ClassDetailsNoSectionsQuery["activity"]["leader"];
    onEnrollClick: (args: { startTime: Date; orgTeacherUid?: string }) => void;
  }) {
    return !!activityWithEnrollableTimes ? (
      <ActivityEnrollableTimes
        activity={activityWithEnrollableTimes}
        leader={leader}
        onEnrollClick={onEnrollClick}
      />
    ) : (
      <></>
    );
  }
);

export const AvailableTimesMemo = memo(function AvailableTimesBody({
  activity,
  isMounted,
  isCrossSellVariant,
  isOrganizationEnrollment,
  onClickMoreLikeThis
}: {
  activity: ClassDetailsQuery_ActivityFragment;
  isMounted: boolean;
  isCrossSellVariant?: boolean;
  isOrganizationEnrollment: boolean;
  onClickMoreLikeThis: () => void;
}) {
  if (!isMounted) {
    return null;
  }

  return activity.isSelfPaced ? (
    <SelfPacedAvailableNow activity={activity} />
  ) : (
    <AvailableTimes
      activity={activity}
      isUseMobileRowsPerPage={isCrossSellVariant}
      isOrganizationEnrollment={isOrganizationEnrollment}
      onClickMoreLikeThis={onClickMoreLikeThis}
    />
  );
});

export const TeacherDetailsMemo = memo(function TeacherDetailsBody({
  activity,
  credentialsRef,
  leader,
  onClickViewReviews,
  MessageTeacherButton
}: {
  activity: ClassDetailsQuery_ActivityFragment;
  credentialsRef: React.RefObject<HTMLElement>;
  leader: ClassDetailsNoSectionsQuery["activity"]["leader"];
  onClickViewReviews?: () => void;
  MessageTeacherButton?: React.JSX.Element;
}) {
  return (
    <TeacherDetails
      leader={leader}
      activity={activity}
      MessageTeacherButton={MessageTeacherButton}
      onClickViewReviews={onClickViewReviews}
      credentialsRef={credentialsRef}
      gradientStyles={gradientFadeStyle}
    />
  );
});

export const TeacherHeaderMemo = memo(function TeacherHeaderBody({
  isClub,
  isMounted,
  isOwnerSellerOrg,
  teacherHeaderRef
}: {
  isClub: boolean;
  isMounted: boolean;
  isOwnerSellerOrg: boolean;
  teacherHeaderRef: React.RefObject<HTMLElement>;
}) {
  const { t } = useTranslation(
    "client\\components\\detailPage\\ClassDetailsMainBody\\Components"
  );
  if (!isMounted) {
    return <></>;
  }
  return (
    <Box>
      <Box ref={teacherHeaderRef}>
        <HeadingMarker />
      </Box>
      <Typography
        variant="h2"
        id="teacher"
        sx={{ fontWeight: 700, marginBottom: "1.6rem" }}
      >
        {isClub
          ? t("Group Leader")
          : isOwnerSellerOrg
          ? t("Offered by")
          : t("Meet the teacher")}
      </Typography>
    </Box>
  );
});

export const TeacherSectionMemo = memo(function TeacherSectionBody({
  TeacherDetails,
  TeacherHeader
}: {
  TeacherDetails: React.JSX.Element;
  TeacherHeader: React.JSX.Element;
}) {
  return (
    <Box
      sx={(theme: Theme) => ({
        [theme.breakpoints.down("md")]: {
          display: "flex",
          flexDirection: "column",
          gap: "2.4rem"
        }
      })}
    >
      {TeacherHeader}
      {TeacherDetails}
    </Box>
  );
});
